import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Filter, Gamepad2, Languages, Music, Printer, Search, Users } from "lucide-react";

const resources = [
  {
    id: "alap-bayt",
    title: "Ancient Abaryt Alap Bayt",
    description: "A full letter-by-letter lesson using the ancient pictographs, sounds, numbers and memory meanings.",
    href: "/free-resources/alap-bayt",
    ages: ["Early Learners", "Youth", "Teens", "Adults", "Household"],
    subjects: ["Abaryt"],
    types: ["Lesson", "Reference"],
    status: "open",
    icon: Languages,
  },
  {
    id: "alap-bayt-printables",
    title: "Alap Bayt Printables",
    description: "Tracing, matching, flashcards and review sheets tied directly to the Alap Bayt lesson.",
    ages: ["Early Learners", "Youth"],
    subjects: ["Abaryt"],
    types: ["Printable", "Activity"],
    status: "coming",
    icon: Printer,
  },
  {
    id: "turah-activities",
    title: "Turah Learning Activities",
    description: "Hands-on review activities for laws, statutes, commands and appointed times.",
    ages: ["Youth", "Teens", "Household"],
    subjects: ["Turah"],
    types: ["Activity", "Game"],
    status: "coming",
    icon: BookOpen,
  },
  {
    id: "language-learning",
    title: "Reading & Language",
    description: "Letters, words, reading, writing and parts of speech arranged by learning level.",
    ages: ["Early Learners", "Youth"],
    subjects: ["Reading & Language"],
    types: ["Lesson", "Printable"],
    status: "coming",
    icon: BookOpen,
  },
  {
    id: "numbers",
    title: "Numbers & Math",
    description: "Number recognition, counting, practice and later math resources organized by level.",
    ages: ["Early Learners", "Youth", "Teens"],
    subjects: ["Math & Numbers"],
    types: ["Lesson", "Printable", "Game"],
    status: "coming",
    icon: Gamepad2,
  },
  {
    id: "yah-girls-companions",
    title: "YaHUaH's Girls Companion Activities",
    description: "Activities that connect directly to YaHUaH's Girls songs, teaching videos and learning episodes.",
    ages: ["Early Learners", "Youth"],
    subjects: ["Turah", "Abaryt", "Reading & Language", "Math & Numbers"],
    types: ["Activity", "Audio / Video"],
    status: "coming",
    icon: Music,
  },
];

const ageOptions = ["All ages", "Early Learners", "Youth", "Teens", "Adults", "Household"];
const subjectOptions = ["All subjects", "Turah", "Abaryt", "Reading & Language", "Math & Numbers", "Practical Skills", "Music & Media"];
const typeOptions = ["All types", "Lesson", "Printable", "Game", "Activity", "Reference", "Audio / Video"];

export default function FreeResources(){
  const [age,setAge]=useState("All ages");
  const [subject,setSubject]=useState("All subjects");
  const [type,setType]=useState("All types");
  const [query,setQuery]=useState("");

  const visible = useMemo(() => resources.filter(resource => {
    const ageMatch = age === "All ages" || resource.ages.includes(age);
    const subjectMatch = subject === "All subjects" || resource.subjects.includes(subject);
    const typeMatch = type === "All types" || resource.types.includes(type);
    const q = query.trim().toLowerCase();
    const searchMatch = !q || [resource.title,resource.description,...resource.ages,...resource.subjects,...resource.types].join(" ").toLowerCase().includes(q);
    return ageMatch && subjectMatch && typeMatch && searchMatch;
  }),[age,subject,type,query]);

  const selectClass="min-h-11 w-full border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 focus:border-kingdom-gold focus:outline-none";

  return <div className="min-h-screen bg-[#fff8ed]" data-testid="free-resources-page">
    <section className="border-b-4 border-kingdom-gold bg-royal-blue py-16 text-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <span className="text-xs font-black uppercase tracking-[.2em] text-kingdom-gold">Open learning library</span>
        <h1 className="mt-3 font-heading text-4xl font-black sm:text-6xl">Free Resources</h1>
        <p className="mt-4 max-w-3xl text-lg leading-relaxed text-white/85">
          Free lessons, printables, games, activities and reference material for different ages and learning needs. This library is not limited to youth.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link to="/ancient-path-youth" className="inline-flex min-h-11 items-center justify-center bg-kingdom-gold px-5 py-3 font-black text-royal-blue">Ancient Path Youth <ArrowRight className="ml-2 h-4 w-4"/></Link>
          <Link to="/YaHsgirls" className="inline-flex min-h-11 items-center justify-center border border-white px-5 py-3 font-bold text-white hover:bg-white/10">YaHUaH&apos;s Girls</Link>
        </div>
      </div>
    </section>

    <section className="border-b border-slate-200 bg-white py-7">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-4 flex items-center gap-2 text-sm font-black uppercase tracking-[.16em] text-royal-blue"><Filter className="h-4 w-4"/>Find what you need</div>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <label className="relative">
            <span className="sr-only">Search resources</span>
            <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400"/>
            <input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search resources..." className="min-h-11 w-full border border-slate-300 bg-white pl-10 pr-3 text-sm focus:border-kingdom-gold focus:outline-none"/>
          </label>
          <select aria-label="Filter by age group" value={age} onChange={e=>setAge(e.target.value)} className={selectClass}>{ageOptions.map(option=><option key={option}>{option}</option>)}</select>
          <select aria-label="Filter by subject" value={subject} onChange={e=>setSubject(e.target.value)} className={selectClass}>{subjectOptions.map(option=><option key={option}>{option}</option>)}</select>
          <select aria-label="Filter by resource type" value={type} onChange={e=>setType(e.target.value)} className={selectClass}>{typeOptions.map(option=><option key={option}>{option}</option>)}</select>
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          <button onClick={()=>{setAge("All ages");setSubject("All subjects");setType("All types");setQuery("");}} className="border border-slate-300 px-3 py-1.5 font-bold text-slate-600 hover:border-royal-blue hover:text-royal-blue">Clear filters</button>
          <span className="px-2 py-1.5 font-semibold text-slate-500">{visible.length} resource{visible.length===1?"":"s"} shown</span>
        </div>
      </div>
    </section>

    <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      {visible.length === 0 ? (
        <div className="bg-white p-10 text-center shadow-sm ring-1 ring-black/5">
          <h2 className="font-heading text-2xl font-black text-royal-blue">No resources match those filters yet.</h2>
          <p className="mt-2 text-slate-600">Clear a filter or choose another age group, subject or resource type.</p>
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {visible.map(resource=>{
            const Icon=resource.icon;
            const card=<>
              <div className="flex items-start justify-between gap-4">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-kingdom-gold/15 text-royal-blue"><Icon className="h-6 w-6"/></div>
                <span className={resource.status==="open"?"bg-kingdom-gold px-2 py-1 text-[10px] font-black uppercase text-royal-blue":"bg-slate-100 px-2 py-1 text-[10px] font-black uppercase text-slate-500"}>{resource.status==="open"?"Open now":"Coming"}</span>
              </div>
              <h2 className="mt-5 font-heading text-2xl font-black text-royal-blue">{resource.title}</h2>
              <p className="mt-2 leading-relaxed text-slate-600">{resource.description}</p>
              <div className="mt-5 space-y-2 text-xs">
                <div><span className="font-black uppercase tracking-wider text-slate-400">Age:</span> <span className="text-slate-600">{resource.ages.join(" · ")}</span></div>
                <div><span className="font-black uppercase tracking-wider text-slate-400">Subject:</span> <span className="text-slate-600">{resource.subjects.join(" · ")}</span></div>
                <div><span className="font-black uppercase tracking-wider text-slate-400">Type:</span> <span className="text-slate-600">{resource.types.join(" · ")}</span></div>
              </div>
              {resource.href && <span className="mt-6 inline-flex items-center font-bold text-royal-blue">Open resource <ArrowRight className="ml-2 h-4 w-4"/></span>}
            </>;
            return resource.href
              ? <Link key={resource.id} to={resource.href} className="bg-white p-6 shadow-sm ring-1 ring-black/5 transition-transform hover:-translate-y-1">{card}</Link>
              : <div key={resource.id} className="bg-white p-6 shadow-sm ring-1 ring-black/5">{card}</div>;
          })}
        </div>
      )}

      <section className="mt-14 grid gap-5 md:grid-cols-2">
        <Link to="/ancient-path-youth" className="bg-royal-blue p-7 text-white shadow-sm">
          <Users className="h-9 w-9 text-kingdom-gold"/>
          <h2 className="mt-4 font-heading text-3xl font-black">Ancient Path Youth</h2>
          <p className="mt-2 text-white/80">A focused learning path for younger learners using selected resources from this larger library.</p>
          <span className="mt-5 inline-flex items-center font-bold text-kingdom-gold">Enter youth learning <ArrowRight className="ml-2 h-4 w-4"/></span>
        </Link>
        <Link to="/YaHsgirls" className="bg-white p-7 shadow-sm ring-1 ring-black/5">
          <Music className="h-9 w-9 text-kingdom-gold"/>
          <h2 className="mt-4 font-heading text-3xl font-black text-royal-blue">Learn with YaHUaH&apos;s Girls</h2>
          <p className="mt-2 text-slate-600">Songs and teaching videos can point directly into matching resources here without making the whole library part of the artist page.</p>
          <span className="mt-5 inline-flex items-center font-bold text-royal-blue">Visit the artist page <ArrowRight className="ml-2 h-4 w-4"/></span>
        </Link>
      </section>
    </main>
  </div>
}
